import React, { useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { ExperimentConfig } from '@atlassian/search-experiment/types';

import type { ApplicationModes } from '../../../../common/constants/application-modes';
import { type PrimaryProductKey, ProductKeys } from '../../../../common/constants/product-keys';
import type { UserDetailsWithStatus } from '../../../../common/constants/types';
import Feedback from '../../../../common/ui/feedback';
import { MessageBannerGrid } from '../../../../common/ui/message-banner-grid';
import { onInlineMessageBannerDismissed } from '../../../../common/utils/events/inline-message-banner-dismissed';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../controllers/analytics';
import { ErrorBoundary } from '../../../../controllers/error-boundary';

import { messages } from './messages';

const styles = cssMap({
	actions: {
		flexBasis: 'fit-content',
	},
	action: {
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
	},
});

type NewProductType = Extract<
	ProductKeys,
	'bitbucket' | 'jira-product-discovery' | 'jira-servicedesk'
>;
const newProducts = {
	[ProductKeys.Bitbucket]: 'Bitbucket',
	[ProductKeys.JiraProductDiscovery]: 'Jira Product Discovery',
	[ProductKeys.JiraServiceManagement]: 'Jira Service Management',
} as Record<NewProductType, string>;

export type NewProductMessageBannerProps = {
	searchQuery: string;
	selectedProduct: ProductKeys;
	selectedProducts: ProductKeys[];
	primaryProduct: PrimaryProductKey | undefined;
	cloudId: string | undefined;
	experimentConfig: ExperimentConfig;
	user: UserDetailsWithStatus;
	applicationMode: ApplicationModes;
	testId?: string;
};

export const NewProductMessageBanner = ({
	searchQuery,
	selectedProduct,
	selectedProducts,
	primaryProduct,
	cloudId,
	experimentConfig,
	user,
	applicationMode,
	testId,
}: NewProductMessageBannerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const [trackSearchQuery, setTrackSearchQuery] = useState(searchQuery);
	const [showProductBanner, setShowProductBanner] = useState({
		[ProductKeys.Bitbucket]: true,
		[ProductKeys.JiraProductDiscovery]: true,
		[ProductKeys.JiraServiceManagement]: true,
	});

	useEffect(() => {
		// reset to default state
		if (searchQuery !== trackSearchQuery) {
			setShowProductBanner({
				[ProductKeys.Bitbucket]: true,
				[ProductKeys.JiraProductDiscovery]: true,
				[ProductKeys.JiraServiceManagement]: true,
			});
			setTrackSearchQuery(searchQuery);
		}
	}, [searchQuery, trackSearchQuery]);

	const onDismiss = () => {
		fireAnalyticsEvent(
			onInlineMessageBannerDismissed({ commonAttributes, nonPrivacySafeAttributes }),
		);
		setShowProductBanner((prev) => ({ ...prev, [selectedProduct]: false }));
	};

	return (
		<>
			{selectedProduct in showProductBanner &&
				showProductBanner[selectedProduct as NewProductType] && (
					<MessageBannerGrid testId={testId}>
						<StatusInformationIcon label="information" color="var(--ds-icon-brand)" />
						<Inline space="space.050" alignBlock="center" shouldWrap>
							<Text size="medium">
								<FormattedMessage
									{...messages.bannerText}
									values={{ product: <b>{newProducts[selectedProduct as NewProductType]}</b> }}
								/>
							</Text>
						</Inline>
						<Inline
							testId={testId && `${testId}--actions`}
							space="space.150"
							rowSpace="space.0"
							alignBlock="center"
							xcss={styles.actions}
						>
							<Box xcss={styles.action}>
								<ErrorBoundary section="SearchPageNewProductMessageBanner">
									<Feedback
										source="search-page-new-product-banner"
										cloudId={cloudId || ''}
										experimentConfig={experimentConfig}
										primaryProduct={primaryProduct}
										user={user}
										applicationMode={applicationMode}
										selectedProducts={selectedProducts}
										isCQL={commonAttributes.isCQL}
									/>
								</ErrorBoundary>
							</Box>
							<IconButton
								icon={CrossIcon}
								onClick={onDismiss}
								label={formatMessage(messages.dismissMessage)}
								appearance={'subtle'}
								spacing={'compact'}
							/>
						</Inline>
					</MessageBannerGrid>
				)}
		</>
	);
};
